import { resolveExecutor, getLogger, type GqlExecutor } from '@hopdrive/sdk-core'
import {
  DeleteExpiredEventsByEventKeyDocument,
  type DeleteExpiredEventsByEventKeyMutation,
} from '@gql/schema'
import type { EventKey } from '../../types/event-keys'
import { eventRegistry } from '../../registry'

/** Result of a purge operation. */
export interface DeleteExpiredEventsResult {
  /** Total number of expired event rows deleted across all event keys. */
  totalDeleted: number
}

/**
 * Delete event log entries that have exceeded their configured expiry.
 *
 * Uses the SDK registry as the source of truth for expiry values.
 * Keys with `null` expiry are never purged.
 *
 * @param executor - Optional GraphQL executor override. Falls back to the default executor set via `init()`.
 * @returns A {@link DeleteExpiredEventsResult} with the total number of rows deleted.
 *
 * @example
 * ```typescript
 * const { totalDeleted } = await deleteExpiredEvents()
 * console.log(`Purged ${totalDeleted} expired events`)
 * ```
 */
export async function deleteExpiredEvents(
  executor?: GqlExecutor
): Promise<DeleteExpiredEventsResult> {
  const exec = resolveExecutor(executor)

  const expiryEntries: Array<{ key: EventKey; expiry: number }> = []

  for (const [key, def] of eventRegistry) {
    if (def.expiry === null) continue
    expiryEntries.push({ key, expiry: def.expiry })
  }

  let totalDeleted = 0

  for (const entry of expiryEntries) {
    const nowSeconds = Date.now() / 1000
    const cutoffMs = (nowSeconds - entry.expiry) * 1000
    const before = new Date(cutoffMs).toISOString()

    const result = await exec.mutate<DeleteExpiredEventsByEventKeyMutation>(
      DeleteExpiredEventsByEventKeyDocument,
      { event_key: entry.key, before }
    )

    const affected = result.delete_eventlogs?.affected_rows ?? 0
    totalDeleted += affected

    if (affected > 0) {
      getLogger().debug('event.purged', {
        event_key: entry.key,
        deleted: affected,
        before,
      })
    }
  }

  getLogger().debug('event.purgeComplete', { totalDeleted })

  return { totalDeleted }
}
